/**
 * /zh-tw/product-brief-template — 產品簡報範本 (Traditional Chinese).
 *
 * Template sibling for the zh-tw cluster; the Simplified twin
 * (/zh/product-brief-template) is our best-ranking zh URL (avg pos ~3).
 * Targets 「產品簡報 範本」「product brief 範本」「一頁式產品企劃」.
 * Mirrors ProductBriefTemplateZh.tsx in Traditional with Taiwan terminology.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

const SECTIONS: Array<{ name: string; desc: string }> = [
  { name: '一句話定位', desc: '這是什麼？一句同事聽一遍就能準確轉述的話。需要第二句才講得清，代表想法還沒壓縮到位。' },
  { name: '為什麼是現在', desc: '什麼變了——新技術、新習慣、新限制。「我們終於有空了」不算 why-now。' },
  { name: '給誰做', desc: '第一批使用者是誰、明確不給誰做。受眾越窄，後面每一節越鋒利。' },
  { name: 'V1 範圍', desc: '能驗證定位的最小版本：做什麼、明確不做什麼。在這裡砍掉的每一項，都是日後可以指著看的「非目標」。' },
  { name: '風險與假設', desc: '哪兩三條假設一旦錯了，整個想法就不成立。還沒驗證的，標出來。' },
  { name: '成功標準', desc: '「做成了」長什麼樣——具體、帶日期、可對照複盤。' },
];

export function ZhTwProductBriefTemplate() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/product-brief-template"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="產品簡報範本：一頁紙，始終最新 · Draftlize"
      documentDescription="一份可以直接抄走的產品簡報（product brief）範本——一句話定位、為什麼是現在、給誰做、V1 範圍、風險與假設、成功標準。寫在文件裡的簡報填完就開始過時；在 Draftlize 裡每一節是一張結構化卡片，定位一改，依賴它的範圍與風險自動標記 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">範本 · 產品簡報</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 60px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            產品簡報範本——<br />
            <em>一頁紙，講清楚為什麼要做。</em>
          </h1>
          <p className="lede">
            產品簡報（product brief）是「為什麼要做這件事」的壓縮論證：一頁紙，六個區塊，
            給主管、工程、設計在動工前對齊。下面是可以直接抄走的<em>產品簡報範本</em>——
            但先知道它的天性：寫在 Notion 或簡報軟體裡的 brief，填完那一刻就開始漂移。
            這一頁先給範本，再給讓它保持最新的做法。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">六個區塊。<em>一頁裝得下。</em></h2>
        </div>
        <div style={{ maxWidth: 820 }}>
          {SECTIONS.map((s, i) => (
            <div
              key={s.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 16,
                padding: '14px 0',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
              }}
            >
              <strong className="serif" style={{ fontSize: 17, color: 'var(--ink-0)' }}>{s.name}</strong>
              <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.7, color: 'var(--ink-1)' }}>{s.desc}</p>
            </div>
          ))}
          <p style={{ fontSize: 15.5, lineHeight: 1.7, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            簡報寫完、方向拍板之後，才進入完整的 PRD——分工上 brief 回答「為什麼做」，
            PRD 回答「做成什麼樣」。重大取捨記得同步落進<a href="/zh-tw/decision-log" style={{ color: 'var(--amber)' }}>決策紀錄</a>。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">一頁紙的問題：<em>它不會自己更新。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">六個區塊互相依賴，文件裡卻互不相連</h3>
            <p>V1 範圍建立在一句話定位上，風險建立在範圍上。定位改了，範圍和風險照理都該重看——但在文件裡它們只是前後排列的段落，誰也不會提醒誰。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">「假設」寫下來之後就沒人管了</h3>
            <p>風險區塊列了三條假設，然後呢？驗證了沒有、被推翻了沒有，文件不知道。最需要追蹤的區塊，恰好是最靜態的區塊。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">簡報最準的一刻，是寫完的那一刻</h3>
            <p>兩週後方向微調、一個月後範圍砍半——brief 還停在第一版。新同事照著讀，讀到的是一個月前的產品。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">同一份範本，<em>變成活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每一節是一張卡片，依賴是真實連結</h3>
            <p>在 Draftlize 裡，定位、範圍、假設各自是結構化卡片：範圍卡連著定位卡、風險卡連著範圍卡。一頁紙變成一張小小的依賴圖。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">定位一改，下游自動標記 stale</h3>
            <p>改了一句話定位，依賴它的 V1 範圍、風險假設自動標記過時——該重看哪幾節，系統直接亮給你看，不用靠記性。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">和 agent 一起把空格填掉</h3>
            <p>六個區塊預先建成引導卡片，你在對話裡回答，agent 幫你落成結構——沒想清楚的部分自動掛成 gap，擋在明面上，而不是埋在條列式清單裡。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          一頁紙的價值在於「大家讀的是同一頁」。前提是，那一頁得跟得上現實。
        </blockquote>
        <cite>區塊照抄。讓依賴關係替你盯著現實。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">常見問題。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '產品簡報（product brief）和 PRD 有什麼差別？', a: 'brief 回答「為什麼要做」：一頁紙、六個區塊，在動工前對齊方向。PRD 回答「做成什麼樣」：完整的需求、使用者故事、驗收標準。先有 brief 拍板，再展開 PRD——順序反了，PRD 會寫得又長又沒有立場。' },
            { q: '產品簡報範本要包含哪些區塊？', a: '六個：一句話定位、為什麼是現在、給誰做、V1 範圍（含明確不做什麼）、風險與假設、成功標準。能一頁裝下就別寫兩頁——brief 的價值就在壓縮。' },
            { q: '產品簡報應該多長？', a: '一頁。超過一頁通常代表兩件事之一：想法還沒壓縮到位，或者你在寫的其實是 PRD。每個區塊兩三句話，讓人五分鐘讀完、讀完能轉述。' },
            { q: 'Draftlize 的產品簡報範本有什麼不同？', a: '同樣六個區塊，但每一節是一張可定位的卡片，依賴是真實連結：定位一改，範圍和風險自動標記 stale。一鍵把範本開成專案，引導卡片預先建好，和 agent 對話著把它填完。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <TemplateCta template="product-brief" locale="zhtw" />
    </MarketingShell>
  );
}
